import React, { useMemo } from 'react';
import { DEGREE_LABELS } from '../../lib/theory';
import {
  buildScaleNodes,
  buildChordNodes,
  INLAY_FRETS,
  DOUBLE_INLAY_FRETS,
} from '../../lib/banjo-theory';
import { useIsDesktop, useIsLandscapePhone } from '../../hooks/useIsDesktop';

// ── Layout (boards 18 + 20, same look as the guitar) ──────────────────────────
//
// The SVG scales to its container, so every number below is a viewBox unit.
//
// PHONE is authored 1:1 with board 18: rendered at the board's 358px width the
// viewBox scale is ~1, so strings land 28px apart and dots are 21px (75% of the gap).
// Frets 0-12, equal-width frets (~26px each; the old taper crushed frets 8-12).
//
// DESKTOP is board 20: frets 0-22 (the banjo's full neck, as before), equal-width frets
// (same as the phone), 36px dots on a 49.2px string gap (73%). The board is
// 880 wide so a typical main-area width (~900px) renders it near 1:1; it scales to fill any width.
//
// Strings run top to bottom: string 0 is the 1st string (D) at the TOP, string 3 the 4th (low D),
// string 4 the short 5th (g) at the bottom. The 5th string only starts at fret 5.

const NUM_STRINGS = 5;
const FIFTH_STRING = 4;
const FIFTH_STRING_START_FRET = 5;
// Open-string names, same order as the string indices; the 5th string is the short high g.
const OPEN_STRING_NAMES = ['D', 'B', 'G', 'D', 'g'];

const PHONE = {
  frets: 12,
  left: 16,          // strings start here; the string names sit in the 0-12 gutter
  boardWidth: 338,   // fret 0 slot through the end of fret 12
  gap: 28,
  pad: 12,
  nutW: 4,
  noteR: 10.5,       // outer radius (21px dot incl. ring), 75% of the 28 string gap
  ring: 1.25,
  font: 9,
  fontSmall: 8,      // labels longer than 2 chars (e.g. "bb7")
  inlayR: 4,
  stringThick: [1.1, 1.5, 1.9, 2.4, 1.1],
  nameX: 6,
  nameFont: 11,
  fifthNameGap: 9,   // distance from the 5th string's peg to its name
  numFont: 11,
  numTop: 3,         // gap between the board bottom and the fret-number line
  numLine: 15,
  bottom: 20,        // room under the board for fret numbers
  viewLeft: 0,
  viewRightPad: 2,
};

// LANDSCAPE is a phone held sideways (useIsLandscapePhone): frets 0-15 like the desktop, equal
// frets (~52px) and a ~34px string gap (roomier than the phone, for bigger dots), so the whole
// neck fits in ~215px of height and the board needs no scrolling. Authored ~1:1 at a typical 812px-wide landscape screen.
const LANDSCAPE = {
  ...PHONE,
  frets: 15,
  boardWidth: 800,
  gap: 34,
  noteR: 13,         // 26px dot incl. ring, 76% of the 34 string gap
  font: 11,
  fontSmall: 10,
  inlayR: 4.5,
  nameFont: 12,
  numFont: 12,
};

const DESKTOP = {
  frets: 22,
  left: 0,
  boardWidth: 880,
  gap: 49.2,
  pad: 27,
  nutW: 5,
  noteR: 18,         // 36px dot incl. ring, 73% of the gap
  ring: 1.25,
  font: 13,
  fontSmall: 11,
  inlayR: 5,
  stringThick: [1.4, 1.9, 2.4, 3, 1.4],
  nameX: -11,
  nameFont: 13,
  fifthNameGap: 13,
  numFont: 13,
  numTop: 10,
  numLine: 18,
  bottom: 30,
  viewLeft: -22,     // gutter for the open-string names
  viewRightPad: 4,   // the last slot is narrower than a dot, so leave room for the fret-22 dot
};

/**
 * Slot widths (viewBox units) for fret 0..frets. Slot 0 is the open-string zone left of the nut.
 * Every slot is the same width, on every layout (Oct 2026): a real-neck taper squeezed the high
 * frets narrower than the dots on phones, and desktop was switched to match.
 */
function fretWidths(L) {
  return Array.from({ length: L.frets + 1 }, () => L.boardWidth / (L.frets + 1));
}

function fretPositions(widths, left) {
  const positions = [left];
  for (let i = 1; i < widths.length; i++) {
    positions.push(positions[i - 1] + widths[i - 1]);
  }
  return positions;
}

// ── Formula resolution ────────────────────────────────────────────────────────

/** Translate Structure + Tonality → theory-engine formula name */
function resolveFormulaName(structure, tonality, modeName = 'Ionian') {
  if (structure === 'Modes')     return modeName;
  const isMajor = tonality.toLowerCase() === 'major';
  if (structure === 'Pentatonic') return isMajor ? 'Major Pentatonic' : 'Minor Pentatonic';
  // Full Scale (default)
  return isMajor ? 'Major Scale' : 'Minor Scale';
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function BanjoFretboard({
  banjoKey,
  banjoTonality,
  banjoStructure,   // 'Full Scale' | 'Pentatonic' | 'Modes' | 'Chords'
  banjoMode,        // diatonic mode name, used when banjoStructure === 'Modes'
  banjoShape,       // 'F Shape' | 'D Shape' | 'Barre Shape', used when banjoStructure === 'Chords'
  banjoStringFocus,
  banjoFilter,
  banjoLabel,
}) {
  const isDesktop = useIsDesktop();
  const isLandscape = useIsLandscapePhone();
  const L = isDesktop ? DESKTOP : isLandscape ? LANDSCAPE : PHONE;
  const DISPLAY_FRETS = L.frets;

  const activeNodes = useMemo(() => {
    if (banjoStructure === 'Chords') {
      return buildChordNodes(banjoKey, banjoShape, banjoTonality, banjoStringFocus);
    }
    const formulaName = resolveFormulaName(banjoStructure, banjoTonality, banjoMode);
    return buildScaleNodes(banjoKey, formulaName, banjoStringFocus, banjoTonality);
  }, [banjoKey, banjoTonality, banjoStructure, banjoMode, banjoShape, banjoStringFocus]);

  // The Filter quick button is greyed for Chords (every chord tone always shows), so it is ignored there.
  const visibleNodes = useMemo(() => {
    if (banjoStructure === 'Chords') return activeNodes;
    if (banjoFilter === 'Roots Only')  return activeNodes.filter(n => n.isRoot);
    if (banjoFilter === 'Triads Only') return activeNodes.filter(n => [0, 3, 4, 7].includes(n.interval));
    return activeNodes;
  }, [activeNodes, banjoFilter, banjoStructure]);

  // Geometry
  const widths     = useMemo(() => fretWidths(L), [L]);
  const xPositions = useMemo(() => fretPositions(widths, L.left), [widths, L]);
  const right      = xPositions[DISPLAY_FRETS] + widths[DISPLAY_FRETS]; // end of the last fret
  const nutX       = xPositions[1];
  const fifthX     = xPositions[FIFTH_STRING_START_FRET]; // where the short 5th string begins

  const neckH   = L.pad * 2 + (NUM_STRINGS - 1) * L.gap;
  const stringY = (sIdx) => L.pad + sIdx * L.gap;
  // The four full-length strings sit in the main board; the 5th string rides in a strip below it
  // that only starts at fret 5.
  const mainBottom = stringY(FIFTH_STRING - 1) + L.pad;
  const viewW   = right + L.viewRightPad - L.viewLeft;
  const viewH   = neckH + L.bottom;

  const noteX = (fretIdx) => xPositions[fretIdx] + widths[fretIdx] / 2;

  const noteLabel = (node) => {
    if (banjoLabel === 'Intervals')  return DEGREE_LABELS[node.interval] ?? '';
    if (banjoLabel === 'Note Names') return node.noteName;
    return '';
  };

  const inlayFrets = INLAY_FRETS.filter(f => f <= DISPLAY_FRETS);
  // Inlays sit between the 2nd and 3rd strings (the middle of the four full-length strings).
  const inlayY = (stringY(1) + stringY(2)) / 2;

  // One note dot: roots are solid terracotta with a white label; scale tones are
  // white with a sage ring and an ink label. Flat — the boards have no shadow.
  const renderDot = (node) => {
    const cx = noteX(node.fret);
    const cy = stringY(node.string);
    const label = noteLabel(node);
    return (
      <g
        key={`banjo-note-${node.string}-${node.fret}-${banjoKey}-${banjoStructure}-${banjoTonality}`}
        className="animate-note-spring"
        style={{ animationDelay: `${node.fret * 15}ms` }}
      >
        <circle
          cx={cx} cy={cy} r={L.noteR - L.ring / 2}
          strokeWidth={L.ring}
          style={{
            fill:   node.isRoot ? 'hsl(var(--root))' : 'hsl(var(--note-scale-fill))',
            stroke: node.isRoot ? 'hsl(var(--root))' : 'hsl(var(--sage))',
          }}
        />
        {label !== '' && label != null && (
          <text
            x={cx} y={cy}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={String(label).length > 2 ? L.fontSmall : L.font}
            fontWeight={800}
            className="pointer-events-none"
            style={{ fill: node.isRoot ? 'hsl(var(--primary-foreground))' : 'hsl(var(--ink))' }}
          >
            {label}
          </text>
        )}
      </g>
    );
  };

  return (
    <div className="w-full">
      <svg
        viewBox={`${L.viewLeft} 0 ${viewW} ${viewH}`}
        style={{ width: '100%', height: 'auto', display: 'block' }}
        className="select-none"
      >
        {/* Board surface with hairline top/bottom edges: the main board, then the 5th-string strip
            (from fret 5) that continues it downwards */}
        <rect x={nutX} y={0} width={right - nutX} height={mainBottom} style={{ fill: 'hsl(var(--board))' }} />
        <rect x={fifthX} y={mainBottom - 1} width={right - fifthX} height={neckH - mainBottom + 1} style={{ fill: 'hsl(var(--board))' }} />
        <rect x={nutX} y={0} width={right - nutX} height={1} style={{ fill: 'hsl(var(--board-edge))' }} />
        <rect x={nutX} y={mainBottom - 1} width={fifthX - nutX} height={1} style={{ fill: 'hsl(var(--board-edge))' }} />
        <rect x={fifthX} y={neckH - 1} width={right - fifthX} height={1} style={{ fill: 'hsl(var(--board-edge))' }} />

        {/* Inlay dots */}
        {inlayFrets.map(f => {
          const cx = noteX(f);
          const dotStyle = { fill: 'hsl(var(--inlay))' };
          if (DOUBLE_INLAY_FRETS.includes(f)) {
            return (
              <g key={`inlay-${f}`}>
                <circle cx={cx} cy={inlayY - L.gap} r={L.inlayR} style={dotStyle} />
                <circle cx={cx} cy={inlayY + L.gap} r={L.inlayR} style={dotStyle} />
              </g>
            );
          }
          return <circle key={`inlay-${f}`} cx={cx} cy={inlayY} r={L.inlayR} style={dotStyle} />;
        })}

        {/* Nut — solid ink, flush against the left of fret 1 (strings 1-4)... */}
        <rect x={nutX - L.nutW} y={0} width={L.nutW} height={mainBottom} style={{ fill: 'hsl(var(--string))' }} />
        {/* ...and the 5th string's own peg-end nut at fret 5 */}
        <rect x={fifthX - L.nutW} y={mainBottom - 1} width={L.nutW} height={neckH - mainBottom + 1} style={{ fill: 'hsl(var(--string))' }} />

        {/* Fret wires — thin grey; the last one closes the end of the board. From fret 5 on they
            also cross the 5th-string strip. */}
        {Array.from({ length: DISPLAY_FRETS - 1 }, (_, i) => i + 2).concat(DISPLAY_FRETS + 1).map(f => {
          const x = f > DISPLAY_FRETS ? right : xPositions[f];
          const full = f > DISPLAY_FRETS || f >= FIFTH_STRING_START_FRET;
          return (
            <rect
              key={`fret-${f}`}
              x={x - 0.5} y={0} width={1} height={full ? neckH : mainBottom}
              style={{ fill: 'hsl(var(--fret))' }}
            />
          );
        })}

        {/* Strings — solid ink; muted strings fade. The 5th string starts at its peg. */}
        {OPEN_STRING_NAMES.map((_, s) => {
          const thick = L.stringThick[s] ?? 1.5;
          const startX = s === FIFTH_STRING ? fifthX : L.left;
          return (
            <rect
              key={`string-${s}`}
              x={startX} y={stringY(s) - thick / 2}
              width={right - startX} height={thick}
              style={{ fill: 'hsl(var(--string))' }}
              opacity={banjoStringFocus[s] ? 1 : 0.2}
            />
          );
        })}

        {/* Open-string names — left of the nut for strings 1-4; the 5th string's name sits just
            left of its own peg */}
        {OPEN_STRING_NAMES.map((name, s) => (
          <text
            key={`string-label-${s}`}
            x={s === FIFTH_STRING ? fifthX - L.nutW - L.fifthNameGap : L.nameX}
            y={stringY(s)}
            textAnchor="middle"
            dominantBaseline="central"
            style={{ fill: 'hsl(var(--ink-muted))' }}
            fontSize={L.nameFont}
            fontWeight={700}
          >
            {name}
          </text>
        ))}

        {/* Note markers */}
        {visibleNodes.filter(node => node.fret <= DISPLAY_FRETS).map(renderDot)}

        {/* Fret numbers — under the board; inlay frets are bold ink, the rest muted */}
        {Array.from({ length: DISPLAY_FRETS + 1 }, (_, i) => i).map(f => {
          const marked = inlayFrets.includes(f);
          return (
            <text
              key={`fnum-${f}`}
              x={noteX(f)} y={neckH + L.numTop + L.numLine / 2}
              textAnchor="middle"
              dominantBaseline="central"
              style={{ fill: marked ? 'hsl(var(--ink))' : 'hsl(var(--ink-muted))' }}
              fontSize={L.numFont}
              fontWeight={marked ? 800 : 600}
              opacity={marked ? 1 : 0.75}
              className="select-none"
            >
              {f}
            </text>
          );
        })}
      </svg>
    </div>
  );
}
